import React, {
  createContext,
  useEffect,
  useContext,
  useState,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import Honeybadger from '@honeybadger-io/js';
import UserStateContext from './UserStateContext';
import MemberContext from './MemberContext';
import RegistryItemGatewayApi from '../services/RegistryItemGatewayApi';
import {getDaysUntilDate} from '../utils/dateUtils';
import noop from '../utils/noop';

const Context = createContext({
  showOutOfStockPrompt: false,
  isDismissed: false,
  isDeterminingOutOfStockPrompt: false,
  dismissOutOfStockPrompt: noop,
  hasOutOfStockItems: false,
});

export default Context;

const shouldDisplayOutOfStock = (daysUntilWedding, outOfStockDismissed) => {
  if (!daysUntilWedding) return false;

  const isWeddingDayClose = daysUntilWedding >= 0 && daysUntilWedding <= 90;

  if (isWeddingDayClose && !outOfStockDismissed) return true;

  const daysSinceDismissed = Math.floor(
    (new Date() - new Date(outOfStockDismissed)) / 8.64e7, // number of milliseconds in 1 day
  );

  if (isWeddingDayClose && daysSinceDismissed > 30) return true;

  return false;
};

export const OutOfStockContextProvider = ({children}) => {
  // Contexts
  const {userStateData} = useContext(UserStateContext);
  const {member, memberId, isFetching: isFetchingMember} = useContext(
    MemberContext,
  );

  // State
  const [isFetchingUnavailable, setIsFetchingUnavailable] = useState(true);
  const [shouldDisplay, setShouldDisplay] = useState(null);
  const [hasOutOfStockItems, setHasOutOfStockItems] = useState(null);
  const showOutOfStockPrompt = hasOutOfStockItems && shouldDisplay;

  // Effects
  useEffect(() => {
    if (!memberId) {
      return;
    }

    const determineHasOutOfStock = async () => {
      setIsFetchingUnavailable(true);
      try {
        const response = await RegistryItemGatewayApi.getRegistryItems({
          memberId,
          status: 'out of stock',
          limit: 1,
        });
        setHasOutOfStockItems(!!response.count);
      } catch (error) {
        setHasOutOfStockItems(false);
        Honeybadger.notify(error, 'GetOutOfStockItemsError');
      }
      setIsFetchingUnavailable(false);
    };

    determineHasOutOfStock();
  }, [memberId]);

  useEffect(() => {
    if (!member.wedding_date || !userStateData) {
      return;
    }

    const determineShouldDisplay = async () => {
      const result = get(userStateData, 'outOfStockDismissed', false);
      const daysUntilWedding = getDaysUntilDate(member.wedding_date);
      const passedDisplayCriteria = shouldDisplayOutOfStock(
        daysUntilWedding,
        result,
      );

      setShouldDisplay(passedDisplayCriteria);
    };

    determineShouldDisplay();
  }, [userStateData, member.wedding_date]);

  // Locals
  const isDeterminingOutOfStockPrompt =
    shouldDisplay === null || isFetchingUnavailable || isFetchingMember;

  const value = useMemo(() => {
    return {
      showOutOfStockPrompt:
        !isDeterminingOutOfStockPrompt && showOutOfStockPrompt,
      isDeterminingOutOfStockPrompt,
      dismissOutOfStockPrompt: () => setShouldDisplay(false),
      hasOutOfStockItems,
    };
  }, [isDeterminingOutOfStockPrompt, showOutOfStockPrompt, hasOutOfStockItems]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

OutOfStockContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
